import React from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { qcTicketApi } from '../api';

export const KAIROS_VIEWER_QC_TICKET = 'KAIROS_VIEWER_QC_TICKET';

export const QC_TICKET_REASONS = [
  { value: 'motion_artifact', label: 'Motion artifact' },
  { value: 'missing_series', label: 'Missing series' },
  { value: 'wrong_protocol', label: 'Wrong protocol' },
  { value: 'incomplete_contrast_phase', label: 'Incomplete contrast phase' },
  { value: 'laterality_issue', label: 'Laterality issue' },
  { value: 'positioning_issue', label: 'Positioning issue' },
  { value: 'repeat_requested', label: 'Repeat requested' },
  { value: 'other', label: 'Other' },
];

const safeString = (value) => (value == null ? '' : String(value)).trim();
const finiteOrNull = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};
const safeErrorDetail = (value) => safeString(value).replace(/<[^>]*>/g, '').slice(0, 200);
const QC_TICKET_REASON_VALUES = new Set(QC_TICKET_REASONS.map((item) => item.value));
const normalizeQcReason = (value) => {
  const clean = safeString(value);
  return QC_TICKET_REASON_VALUES.has(clean) ? clean : "";
};

export const buildQcTicketPayload = ({
  studyInstanceUID,
  activeReportContext,
  activeViewportIndex = 0,
  reason,
  note,
  protocolLabel,
  user,
} = {}) => {
  const ctx = activeReportContext || {};
  const series = ctx.series || {};
  const wl = ctx.windowLevel || {};
  const payload = {
    studyInstanceUID: safeString(studyInstanceUID || ctx.studyInstanceUID),
    seriesInstanceUID: safeString(series.seriesInstanceUID || series.series_iuid || ctx.seriesInstanceUID),
    viewportIndex: finiteOrNull(ctx.viewportIndex ?? activeViewportIndex) ?? 0,
    reason: normalizeQcReason(reason),
    note: safeString(note),
    slice: finiteOrNull(ctx.slice),
    windowLevel: {
      ww: finiteOrNull(wl.ww),
      wc: finiteOrNull(wl.wc),
    },
    protocolLabel: safeString(protocolLabel),
    source: 'dicom-viewer',
    marker: KAIROS_VIEWER_QC_TICKET,
  };
  if (user?.username || user?.email) payload.reportedBy = safeString(user.username || user.email);
  return payload;
};

export const validateQcTicketPayload = (payload) => {
  const issues = [];
  if (!payload?.studyInstanceUID) issues.push('Open a study before creating a QC ticket.');
  if (!payload?.reason) issues.push('Choose a QC reason.');
  if (payload?.reason === 'other' && !safeString(payload?.note)) issues.push('Add a short note for Other QC issues.');
  return issues;
};

const QcTicketDialog = ({
  open,
  onClose,
  studyInstanceUID,
  activeReportContext,
  activeViewportIndex = 0,
  protocolLabel = '',
  user = null,
}) => {
  const [reason, setReason] = React.useState(QC_TICKET_REASONS[0].value);
  const [note, setNote] = React.useState('');
  const [status, setStatus] = React.useState({ state: 'idle', message: '' });

  React.useEffect(() => {
    if (open) {
      setReason(QC_TICKET_REASONS[0].value);
      setNote('');
      setStatus({ state: 'idle', message: '' });
    }
  }, [open]);

  const payload = buildQcTicketPayload({
    studyInstanceUID,
    activeReportContext,
    activeViewportIndex,
    reason,
    note,
    protocolLabel,
    user,
  });
  const issues = validateQcTicketPayload(payload);
  const contextLabel = payload.seriesInstanceUID
    ? `Study ${payload.studyInstanceUID || '—'} · Series ${payload.seriesInstanceUID} · VP ${payload.viewportIndex + 1}${payload.slice != null ? ` · Slice ${payload.slice + 1}` : ''}`
    : `Study ${payload.studyInstanceUID || '—'} · VP ${payload.viewportIndex + 1}`;

  const submit = async () => {
    if (issues.length) {
      setStatus({ state: 'error', message: issues.join(' ') });
      return;
    }
    setStatus({ state: 'saving', message: 'Creating QC ticket…' });
    try {
      await qcTicketApi.create(payload);
      setStatus({ state: 'success', message: 'QC ticket sent to imaging operations.' });
    } catch (error) {
      const detail = safeErrorDetail(error?.response?.data?.detail || error?.message || 'QC ticket endpoint is unavailable.');
      setStatus({ state: 'error', message: `QC ticket was not saved: ${detail}` });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth data-kairos-marker={KAIROS_VIEWER_QC_TICKET}>
      <DialogTitle sx={{ fontSize: 14, letterSpacing: 1, fontFamily: 'monospace' }}>Create QC Ticket</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
        <Typography sx={{ fontSize: 12, color: '#90a4ae' }}>{contextLabel}</Typography>
        <FormControl size="small" fullWidth>
          <InputLabel id="qc-ticket-reason-label">QC reason</InputLabel>
          <Select labelId="qc-ticket-reason-label" label="QC reason" value={reason} onChange={(event) => setReason(event.target.value)}>
            {QC_TICKET_REASONS.map((item) => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField
          label="Clinical operations note"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          multiline
          minRows={3}
          inputProps={{ maxLength: 1000 }}
          helperText="No PHI is required here; include only the acquisition/QC issue needed for ops follow-up."
        />
        {status.state !== 'idle' && (
          <Alert severity={status.state === 'success' ? 'success' : status.state === 'saving' ? 'info' : 'error'}>{status.message}</Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} size="small">Close</Button>
        <Button onClick={submit} size="small" variant="contained" data-testid="qc-ticket-submit" disabled={status.state === 'saving' || status.state === 'success'}>
          Create ticket
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const __test__ = { buildQcTicketPayload, validateQcTicketPayload, safeErrorDetail, normalizeQcReason };
export default QcTicketDialog;
